import { AxiosRequestConfig } from 'axios';
import instance, { ADMIN_PREFIX } from 'services/api';
import {
  CreateDoctorInput,
  DoctorStatusInput,
  PatientListQuery,
  PatientListResponse,
  PatientListRow,
  UpdateUserInput,
  UserInput,
  UserListQuery,
  UserListResponse,
  UserListRow
} from 'types/api/users';

/**
 * Which of the four role tables an id belongs to. They have independent
 * auto-increment ids, so every by-id route needs telling which one.
 */
type UserRoleTable = 'patient' | 'doctor' | 'admin' | 'translator';

/**
 * GET /api/v2/admin/users — paginated user list with optional role/name
 * filters. BE applies the shared toSnake() interceptor (cycle-17) so the
 * response already matches the legacy USER_FRAGMENT snake_case shape.
 */
export const fetchUsers = async (
  query: UserListQuery,
  config?: AxiosRequestConfig
): Promise<UserListResponse> => {
  const params: Record<string, string | number> = {
    page: query.page ?? 1,
    limit: query.limit ?? 10
  };

  if (query.role) params.role = query.role;
  if (query.name) params.name = query.name;

  const res = await instance.get<{ data: UserListRow[]; total: number }>(
    `${ADMIN_PREFIX}/users`,
    { ...(config ?? {}), params: { ...(config?.params ?? {}), ...params } }
  );

  return {
    results: res.data.data ?? [],
    total: res.data.total ?? 0
  };
};

/**
 * GET /api/v2/users/:id?role=… — one user.
 *
 * `role` is required by the API, not merely advisable: the four role tables
 * have independent auto-increment ids, so a bare numeric id is ambiguous,
 * and the backend now answers 400 rather than guessing by precedence. It
 * used to resolve patient -> doctor -> admin -> translator and hand back
 * whichever table had the id first.
 */
export const fetchUserById = async (
  id: string | number,
  role: UserRoleTable
): Promise<UserListRow> => {
  const res = await instance.get<UserListRow>(`${ADMIN_PREFIX}/users/${id}`, {
    params: { role }
  });

  return res.data;
};

/**
 * PUT /api/v2/users/:id/role — give an admin an RBAC role.
 *
 * super_admin only (403 otherwise). REPLACES the admin-route assignment
 * rather than adding one. The holder keeps their previous access until
 * their token refreshes, so this is not immediate for them.
 */
export const assignUserRole = async (
  id: string | number,
  roleId: number
): Promise<{ id: number; roleId: number; roleName: string }> => {
  const res = await instance.put<{
    id: number;
    roleId: number;
    roleName: string;
  }>(`${ADMIN_PREFIX}/users/${id}/role`, { roleId });

  return res.data;
};

export const createUser = (input: UserInput) =>
  instance.post<UserListRow>(`${ADMIN_PREFIX}/users`, input);

export const createDoctor = (
  input: CreateDoctorInput,
  organizationHeader?: string
) =>
  instance.post<UserListRow>(
    `${ADMIN_PREFIX}/users/doctor`,
    input,
    organizationHeader
      ? { headers: { organization: organizationHeader } }
      : undefined
  );

/**
 * PUT /api/v2/users/:id?role=… — edit a user's own fields.
 *
 * `role` IS REQUIRED, AND NOT FOR TIDINESS. The four role tables have
 * independent auto-increment ids, so a bare numeric id is ambiguous:
 * without this the backend resolves patient -> doctor -> admin ->
 * translator by precedence and writes to the first table that has the id.
 * Editing doctor 85 wrote to patient 85. Making it an argument rather than
 * an option is what stops the next call site from leaving it off.
 *
 * UpdateUserDto accepts email, password, firstName, lastName, phone,
 * phoneCode and gender — and the service applies phoneCode only to
 * patients. dateOfBirth is still create-only: the DTO has no field for it,
 * and forbidNonWhitelisted turns an extra key into a 400.
 */
export const updateUser = (
  id: string | number,
  role: UserRoleTable,
  input: UpdateUserInput
) =>
  instance.put<UserListRow>(`${ADMIN_PREFIX}/users/${id}`, input, {
    params: { role }
  });

export const sendDoctorEmail = (email: string) =>
  instance.post<{ message: string }>(`${ADMIN_PREFIX}/users/doctor-email`, {
    email
  });

export const blockUser = (id: string | number) =>
  instance.delete<{ status: boolean }>(`${ADMIN_PREFIX}/users/block/${id}`);

/**
 * DELETE /api/v2/users/:id?role=… — soft-delete (sets removed=true).
 *
 * Same ambiguity as the other two, and the sharpest case: without `role`
 * this once resolved by table precedence, so retiring doctor 42 could
 * retire patient 42 instead. The API answers 400 without it now.
 */
export const deleteUser = (id: string | number, role: UserRoleTable) =>
  instance.delete<{ status: boolean }>(`${ADMIN_PREFIX}/users/${id}`, {
    params: { role }
  });

export const updateAdminPassword = (id: string | number, password: string) =>
  instance.put<{ message: string }>(
    `${ADMIN_PREFIX}/users/update-password-admin/${id}`,
    { password }
  );

export const updateTranslatorPassword = (
  id: string | number,
  password: string
) =>
  instance.put<{ message: string }>(
    `${ADMIN_PREFIX}/users/update-password-translator/${id}`,
    { password }
  );

export const sendUserEmail = (input: {
  user_ids: number[];
  subject: string;
  body: string;
}) =>
  instance.post<{ message: string }>(`${ADMIN_PREFIX}/users/send-email`, input);

/**
 * PUT /api/v2/admin/doctors/:id/status — doctor profile status switch.
 * Maps the legacy UPDATE_DOCTOR_STATUS GraphQL mutation.
 */
export const updateDoctorStatus = (
  id: string | number,
  input: DoctorStatusInput
) => instance.put(`${ADMIN_PREFIX}/doctors/${id}/status`, input);

/**
 * GET /api/v2/admin/patients — rich patient list with the legacy
 * patientsList filter set. Cycle-17 reshaped the DTO to snake_case +
 * subscription filter + hydrated subscribe:{...} on each row.
 */
export const fetchPatients = async (
  query: PatientListQuery,
  config?: AxiosRequestConfig
): Promise<PatientListResponse> => {
  const params: Record<string, string | number> = {
    page: query.page ?? 1,
    limit: query.limit ?? 10
  };

  if (query.search) params.search = query.search;
  if (query.start_date) params.start_date = query.start_date;
  if (query.end_date) params.end_date = query.end_date;
  if (query.birthday_start) params.birthday_start = query.birthday_start;
  if (query.birthday_end) params.birthday_end = query.birthday_end;
  if (query.gender != null) params.gender = query.gender;
  if (query.confirm != null) params.confirm = query.confirm;
  if (query.lang) params.lang = query.lang;
  if (query.subscription != null) params.subscription = query.subscription;

  const res = await instance.get<{
    data: PatientListResponse['results'];
    total: number;
  }>(`${ADMIN_PREFIX}/patients`, {
    ...(config ?? {}),
    params: { ...(config?.params ?? {}), ...params }
  });

  return {
    results: res.data.data ?? [],
    total: res.data.total ?? 0
  };
};

/**
 * GET /api/v2/admin/patients/:id — one patient. Same snake_case row the
 * list returns (the module's shared toSnake interceptor); `id` is the
 * numeric core.patients.id, which the BE parses with ParseIntPipe.
 */
export const fetchPatientById = async (
  id: string | number
): Promise<PatientListRow> => {
  const res = await instance.get<PatientListRow>(
    `${ADMIN_PREFIX}/patients/${id}`
  );

  return res.data;
};
